import { createOutpassSchema } from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { apiList, api } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import {
  Notice,
  Pager,
  errorMessage,
  primaryButton,
  secondaryButton,
} from '../../components/common';
import { TextField } from '../../components/TextField';
import { t } from '../../i18n';
import { type Message, type Person } from './shared';

interface Outpass {
  id: string;
  studentName: string;
  admissionNumber: string;
  leavesOn: string;
  expectedBack: string;
  returnedOn: string | null;
  reason: string;
  status: 'out' | 'returned';
  lateDays: number;
}

const blank = { back: '', reason: '', escort: '' };

export function OutpassesTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const recording = can('hostel.outpass', schoolId);
  const [form, setForm] = useState(blank);
  const [search, setSearch] = useState('');
  const [person, setPerson] = useState<Person | null>(null);
  const [lateOnly, setLateOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<Message>(null);

  const matches = useQuery({
    queryKey: ['hostel', schoolId, 'students', search],
    queryFn: () =>
      apiList<Person>('/students', { query: { schoolId, q: search, pageSize: 6 } }).then(
        (r) => r.data,
      ),
    enabled: recording && search.trim().length >= 2 && !person,
  });
  const passes = useQuery({
    queryKey: ['hostel', schoolId, 'outpasses', lateOnly, page],
    queryFn: () =>
      apiList<Outpass>(`/schools/${schoolId}/hostel/outpasses`, {
        query: { lateOnly: lateOnly ? 'true' : undefined, page, pageSize: 20 },
      }),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['hostel', schoolId] });
  const fail = (e: unknown) => setMessage({ kind: 'error', text: errorMessage(e) });
  const record = useMutation({
    mutationFn: (body: object) =>
      api(`/schools/${schoolId}/hostel/outpasses`, { method: 'POST', body }),
    onSuccess: () => {
      setForm(blank);
      setPerson(null);
      setSearch('');
      setMessage({ kind: 'ok', text: t('ho.passRecorded') });
      refresh();
    },
    onError: fail,
  });
  const back = useMutation({
    mutationFn: (id: string) =>
      api(`/schools/${schoolId}/hostel/outpasses/${id}/return`, { method: 'POST', body: {} }),
    onSuccess: () => {
      setMessage({ kind: 'ok', text: t('ho.returnedOk') });
      refresh();
    },
    onError: fail,
  });

  return (
    <section aria-label={t('ho.tab.outpasses')} className="space-y-6">
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      {recording && (
        <form
          noValidate
          aria-label={t('ho.recordPass')}
          className="grid items-end gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-4"
          onSubmit={(e) => {
            e.preventDefault();
            const parsed = createOutpassSchema.safeParse({
              studentId: person?.id,
              expectedBack: form.back,
              reason: form.reason,
              escort: form.escort,
            });
            const next: Record<string, string> = {};
            if (!parsed.success)
              for (const i of parsed.error.issues) next[String(i.path[0])] = i.message;
            setErrors(next);
            if (!parsed.success) return;
            setMessage(null);
            record.mutate(parsed.data);
          }}
        >
          <div className="relative">
            <TextField
              label={t('ho.findStudent')}
              value={person ? `${person.firstName} ${person.lastName}` : search}
              error={errors.studentId ? t('ho.chooseStudent') : undefined}
              onChange={(e) => {
                setPerson(null);
                setSearch(e.target.value);
              }}
            />
            {!person && matches.data && matches.data.length > 0 && (
              <ul className="absolute z-10 mt-1 w-full divide-y divide-slate-100 rounded border border-slate-200 bg-white text-sm shadow">
                {matches.data.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      className="w-full p-2 text-left hover:bg-slate-50"
                      onClick={() => setPerson(p)}
                    >
                      {p.firstName} {p.lastName} · {p.admissionNumber}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <TextField
            type="date"
            label={t('ho.expectedBack')}
            value={form.back}
            error={errors.expectedBack}
            onChange={(e) => setForm({ ...form, back: e.target.value })}
          />
          <TextField
            label={t('ho.reason')}
            value={form.reason}
            error={errors.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
          />
          <TextField
            label={t('ho.escort')}
            value={form.escort}
            error={errors.escort}
            onChange={(e) => setForm({ ...form, escort: e.target.value })}
          />
          <div className="sm:col-span-4">
            <button type="submit" className={primaryButton} disabled={record.isPending}>
              {t('ho.recordPass')}
            </button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">{t('ho.passes')}</h2>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={lateOnly}
              onChange={(e) => {
                setLateOnly(e.target.checked);
                setPage(1);
              }}
            />
            {t('ho.lateOnly')}
          </label>
        </div>
        {passes.data?.data.length === 0 && <p className="text-slate-600">{t('ho.noPasses')}</p>}
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
          {passes.data?.data.map((p) => (
            <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 p-3">
              <span>
                <span className="font-medium">{p.studentName}</span> · {p.admissionNumber} ·{' '}
                {p.reason} · {p.leavesOn} → {p.returnedOn ?? p.expectedBack}
                {p.status === 'out' ? ` (${t('ho.statusOut')})` : ''}
                {p.lateDays > 0 && (
                  <span className="ml-2 font-medium text-red-700">
                    {t('ho.lateDays', { days: p.lateDays })}
                  </span>
                )}
              </span>
              {recording && p.status === 'out' && (
                <button
                  type="button"
                  className={secondaryButton}
                  aria-label={`${t('ho.markBack')}: ${p.studentName}`}
                  disabled={back.isPending}
                  onClick={() => back.mutate(p.id)}
                >
                  {t('ho.markBack')}
                </button>
              )}
            </li>
          ))}
        </ul>
        {passes.data && passes.data.meta.totalPages > 1 && (
          <Pager page={page} totalPages={passes.data.meta.totalPages} onPage={setPage} />
        )}
      </div>
    </section>
  );
}
